import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UsersService } from '../../../../core/services/user/users.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RolesService } from '../../../../core/services/user/roles.service';
import { ToastComponent } from '../../../shared/toast/toast.component';
import { SpecialismsService } from '../../../../core/services/user/specialisms.service';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import { ToastService } from '../../../../core/services/toast.service';
@Component({
  selector: 'app-create-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ToastComponent,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './create-edit-user.component.html',
  styleUrl: './create-edit-user.component.scss',
})
export class CreateEditUserComponent {
  userForm: FormGroup;
  roles: any = [];
  userId: string = '';
  specialismList: any = [];
  organizationList: any = [];
  organizationBranchList: any = [];
  maxDate: string;
  selectedBranch: any;
  currentUser: any = null;

  constructor(
    private router: Router,
    private usersService: UsersService,
    private rolesService: RolesService,
    private specialismsService: SpecialismsService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private languageService: LanguageService,
    private toastService: ToastService
  ) {
    const userLang = this.languageService.getLanguage() || 'en';
    this.translate.use(userLang);
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.userForm = this.fb.group({
      customerCode: [null, Validators.required], // organization
      specialisms: [null, Validators.required],
      roleId: [null, Validators.required],
      name: ['', [Validators.required, lettersOnlyValidator()]],
      surname: ['', [Validators.required, lettersOnlyValidator()]],
      pid: ['', [Validators.required, Validators.minLength(6)]],
      dateOfBirth: ['', [Validators.required, this.dateValidator]],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^\\+?[0-9]{7,15}$')],
      ],
      email: ['', [Validators.required, Validators.email]],
      branchCode: [null, Validators.required],
      branchName: [null, Validators.required],
      branchSelectedLanguageCode: [null],
      sendReportToPatient: [null],
    });
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.getUserById();
    this.getSpecialisms();
    this.getRoles();
    this.getOrganizations();
  }

  getUserById() {
    this.userId = this.route.snapshot.paramMap.get('id')!;
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe({
        next: (res) => {
          this.userForm.patchValue({
            name: res.name,
            surname: res.surname,
            customerCode: res.customerCode,
            email: res.email,
            phoneNumber: res.phoneNumber,
            pid: res.pid,
            roleId: res.roleId,
            dateOfBirth: res.dateOfBirth.slice(0, 10),
            specialisms: res.userSpecialsms.map(
              (item: any) => item.specialismId
            ),
            branchCode: res.branchCode,
            branchName: res.branchName,
            branchSelectedLanguageCode: res.branchSelectedLanguageCode,
            sendReportToPatient: res.sendReportToPatient,
          });
          this.getBranchesByOrganizationCode(res.customerCode);
        },
      });
    }
  }

  getSpecialisms() {
    this.specialismsService.getSpecialisms().subscribe({
      next: (res) => {
        this.specialismList = res;
      },
    });
  }

  getRoles() {
    this.rolesService.getRoles().subscribe({
      next: (res) => {
        this.roles = res.items;
      },
    });
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }

  saveUser(e: Event) {
    e.preventDefault();
    this.fillFormBranchRelatedFields();
    if (this.userId) {
      // Edit user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatDate(
          this.userForm.value.dateOfBirth
        );

        const formatUserSpecialisms = this.userForm.value.specialisms.map(
          (specialismId: string) => {
            return { id: specialismId };
          }
        );

        const formData = {
          ...this.userForm.value,
          id: this.userId,
          userSpecialisms: formatUserSpecialisms,
        };
        delete formData.specialisms;

        this.usersService.editUser(this.userId, formData).subscribe({
          next: () => {
            this.toastService.showToast(
              'User has been edited successfully',
              'success'
            );

            setTimeout(() => {
              this.router.navigate(['/user/list']);
            }, 1000);
          },
        });
      } else {
        this.toastService.showToast(
          'All fields are required and must be filled with valid values',
          'warning'
        );

        this.userForm.markAllAsTouched();
      }
    } else {
      // Create new user
      if (this.userForm.valid) {
        this.userForm.value.dateOfBirth = this.formatDate(
          this.userForm.value.dateOfBirth
        );

        const formatSpecialisms = [
          {
            id: this.userForm.value.specialisms,
          },
        ];

        const formData = {
          ...this.userForm.value,
          specialisms: formatSpecialisms,
        };

        this.usersService.createUser(formData).subscribe({
          next: () => {
            this.toastService.showToast(
              'User has been created successfully',
              'success'
            );

            setTimeout(() => {
              this.router.navigate(['/user/list']);
            }, 1000);
          },
        });
      } else {
        this.toastService.showToast(
          'All fields are required and must be filled with valid values',
          'warning'
        );
        this.userForm.markAllAsTouched();
      }
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  dateValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;

    const [year, month, day] = value.split('-').map(Number);
    const currentYear = new Date().getFullYear();

    if (year > currentYear || month > 12 || day > 31) {
      return { invalidDate: true };
    }
    return null;
  }

  getBranchesByOrganizationCode(customerCode: string) {
    this.customersService
      .getCustomerBranchesByCustomerCode(customerCode)
      .subscribe({
        next: (res) => {
          this.organizationBranchList = res;
        },
      });
  }

  onChangeCustomerCode(event: any) {
    const selectedOrganization = (event.target as HTMLSelectElement).value;
    this.userForm.patchValue({ branchCode: null });
    this.getBranchesByOrganizationCode(selectedOrganization);
  }

  onChangeSelectedBranch(event: any) {
    const selectedBranchId = (event.target as HTMLSelectElement).value;
    this.selectedBranch = this.organizationBranchList.find(
      (item: any) => item.id == selectedBranchId
    );
  }

  private fillFormBranchRelatedFields(): void {
    if (this.selectedBranch) {
      this.userForm.patchValue({ branchCode: this.selectedBranch.id });
      this.userForm.patchValue({ branchName: this.selectedBranch.name });
      this.userForm.patchValue({
        branchSelectedLanguageCode: this.selectedBranch.languageCode,
      });
      this.userForm.patchValue({
        sendReportToPatient: this.selectedBranch.sendReportToPatient.toString(),
      });
    }
  }
}
